import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { processCardPayment } from "@/services/student/checkout/checkout.service";
import { CheckoutError } from "@/services/student/checkout/checkout.api";
import type { PaymentMethodAdapter, PaymentAdapterContext } from "../types";

type BrickFormData = {
  token: string;
  payment_method_id?: string;
  installments?: number;
  issuer_id?: string | number;
  payer?: {
    email?: string;
    identification?: { type?: string; number?: string };
  };
};

const CARD_PAYMENT_CUSTOMIZATION = {
  paymentMethods: {
    maxInstallments: 1,
    minInstallments: 1,
  },
};

const MP_SECURITY_SCRIPT_ID = "mp-security-js";
const MP_SECURITY_SCRIPT_SRC = "https://www.mercadopago.com/v2/security.js";

function ensureMercadoPagoSecurityScript(): void {
  if (
    document.getElementById(MP_SECURITY_SCRIPT_ID) ||
    document.querySelector(`script[src="${MP_SECURITY_SCRIPT_SRC}"]`)
  ) {
    return;
  }

  const script = document.createElement("script");
  script.id = MP_SECURITY_SCRIPT_ID;
  script.src = MP_SECURITY_SCRIPT_SRC;
  script.setAttribute("view", "checkout");
  document.body.appendChild(script);
}

function readMercadoPagoDeviceId(): string | undefined {
  const value = (window as Window & { MP_DEVICE_SESSION_ID?: unknown }).MP_DEVICE_SESSION_ID;
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

type MercadoPagoCardPaymentBrickProps = {
  amount: number;
  publicKey: string;
  onSubmit: (formData: BrickFormData) => Promise<void>;
  onError: (error: { message?: string }) => void;
};

const MercadoPagoCardPaymentBrick = memo(function MercadoPagoCardPaymentBrick({
  amount,
  publicKey,
  onSubmit,
  onError,
}: MercadoPagoCardPaymentBrickProps) {
  const initialization = useMemo(() => ({ amount }), [amount]);

  return (
    <CardPayment
      key={publicKey}
      initialization={initialization}
      customization={CARD_PAYMENT_CUSTOMIZATION}
      onSubmit={onSubmit}
      onError={onError}
    />
  );
});

const MercadoPagoCardView: React.FC<PaymentAdapterContext> = ({
  session,
  orderId,
  amount,
  onCardSubmit,
  hideTitle = false,
}) => {
  const [ready, setReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const initializedKey = useRef<string | null>(null);
  const orderIdRef = useRef(orderId);
  const onCardSubmitRef = useRef(onCardSubmit);

  orderIdRef.current = orderId;
  onCardSubmitRef.current = onCardSubmit;

  const publicKey = session.publicKey?.trim() ?? "";

  useEffect(() => {
    if (!publicKey) {
      setReady(false);
      setErrorMessage("Chave pública do Mercado Pago indisponível para este pedido.");
      return;
    }

    if (initializedKey.current === publicKey) {
      ensureMercadoPagoSecurityScript();
      setReady(true);
      return;
    }

    initMercadoPago(publicKey, { locale: "pt-BR" });
    ensureMercadoPagoSecurityScript();
    initializedKey.current = publicKey;
    setReady(true);
  }, [publicKey]);

  const handleSubmit = useCallback(async (formData: BrickFormData) => {
    setErrorMessage(null);
    setStatusMessage(null);
    onCardSubmitRef.current?.();

    try {
      const result = await processCardPayment(orderIdRef.current, {
        token: formData.token,
        paymentMethodId: formData.payment_method_id,
        installments: formData.installments ?? 1,
        issuerId:
          formData.issuer_id === undefined || formData.issuer_id === null
            ? undefined
            : String(formData.issuer_id),
        deviceId: readMercadoPagoDeviceId(),
        payer: formData.payer
          ? {
              email: formData.payer.email,
              identification: formData.payer.identification
                ? {
                    type: formData.payer.identification.type,
                    number: formData.payer.identification.number,
                  }
                : undefined,
            }
          : undefined,
      });

      if (result.success) {
        setStatusMessage(result.message ?? "Pagamento enviado. Aguardando confirmação…");
        return;
      }

      setErrorMessage(result.message ?? "Não foi possível processar o pagamento.");
    } catch (error) {
      if (error instanceof CheckoutError) {
        if (error.statusCode === 503 || error.statusCode === 502) {
          setErrorMessage(
            error.message ||
              "Mercado Pago temporariamente indisponível. Tente novamente em instantes."
          );
          throw error;
        }
        if (error.statusCode === 422) {
          setErrorMessage(
            error.message || "Pagamento recusado. Inicie um novo checkout para tentar de novo."
          );
          return;
        }
        setErrorMessage(error.message);
        return;
      }
      setErrorMessage(
        error instanceof Error ? error.message : "Falha ao processar pagamento com cartão."
      );
      throw error;
    }
  }, []);

  const handleBrickError = useCallback((error: { message?: string }) => {
    setErrorMessage(error?.message ?? "Erro no formulário de cartão.");
  }, []);

  return (
    <Card id="mercadopago-card-adapter" tabIndex={-1} className="outline-none">
      {hideTitle ? null : (
        <CardHeader>
          <h2 className="text-xl font-semibold">Cartão (Mercado Pago)</h2>
        </CardHeader>
      )}
      <CardContent className={hideTitle ? "space-y-4 pt-6" : "space-y-4"}>
        <p className="text-text-secondary text-sm">
          Informe os dados do cartão no formulário seguro abaixo. Valor:{" "}
          <strong>R$ {amount.toFixed(2).replace(".", ",")}</strong> (à vista).
        </p>
        <p className="text-xs text-text-tertiary">
          Os dados do cartão são capturados apenas pelo Mercado Pago — nunca enviados à OfCourse.
        </p>

        {errorMessage ? (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
        ) : null}
        {statusMessage ? (
          <p className="text-sm text-success-subtle" role="status">
            {statusMessage}
          </p>
        ) : null}

        {ready && publicKey ? (
          <MercadoPagoCardPaymentBrick
            amount={amount}
            publicKey={publicKey}
            onSubmit={handleSubmit}
            onError={handleBrickError}
          />
        ) : (
          <div className="rounded-lg border border bg-surface p-6 text-center text-sm text-text-secondary">
            <p>Carregando formulário de cartão…</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const MercadoPagoCardAdapter: PaymentMethodAdapter = {
  provider: "MercadoPago",
  method: "Card",
  render: (context) => <MercadoPagoCardView {...context} />,
};
